import React, { Fragment, PropsWithChildren, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { AditionTagWithFallback } from '@vgn-medien-holding/vgn-fe-components';
import { PropsWithClassProps } from '@vgn-medien-holding/vgn-react-components';
import { AditionInitialization } from '@components/atoms/Adition/AditionInitialization';
import { Button } from '@components/atoms/Button/Button';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { StreamingTeaser } from '@components/molecules/StreamingTeaser/StreamingTeaser';
import { getObjectValue } from '@utils/common';
import { adPositions } from '@lib/adition/adPositions';
import { GetMoviesDocument, GetShowsDocument } from '@lib/graphql/generated';
import { usePaginatedQuery } from '@lib/graphql/urql';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const interleave = ([x, ...xs]: any = [], ys: any = []) => (x === undefined ? ys : [x, ...interleave(ys, xs)]);

interface EntityMixProps extends PropsWithChildren, PropsWithClassProps {
  movieVariables: {
    first: number;
    page: number;
    id?: number | string;
    provider?: string[] | [];
    genres?: string[] | [];
    orderBy?: string;
    orderDirection?: string;
  };
  tvshowVariables: {
    first: number;
    page: number;
    id?: number | string;
    provider?: string[] | [];
    genres?: string[] | [];
    orderBy?: string;
    orderDirection?: string;
  };
  movieSkip?: boolean;
  tvshowSkip?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  alternateMovieQuery?: any;
  alternateMoviePath?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  alternateTvshowQuery?: any;
  alternateTvshowPath?: string[];
  onAction?: (fetchInstructions) => void;
}

export const EntityMix = ({
  movieVariables,
  tvshowVariables,
  movieSkip,
  tvshowSkip,
  alternateMovieQuery,
  alternateMoviePath,
  alternateTvshowQuery,
  alternateTvshowPath,
  children,
  classProps,
}: EntityMixProps) => {
  const [
    { data: movieData, fetching: moviesLoading, hasMorePages: hasMoreMoviesPages, fetchNextPage: fetchMoreMovies },
  ] = usePaginatedQuery({
    query: alternateMovieQuery || GetMoviesDocument,
    variables: movieVariables,
    pause: movieSkip,
  });
  const hasMoreMovies = hasMoreMoviesPages && !movieSkip;
  const movieDataPath = alternateMoviePath || ['movies'];
  const movies = movieData?.flatMap((data) => getObjectValue(data, movieDataPath)?.data);

  const [{ data: tvshowData, fetching: showsLoading, hasMorePages: hasMoreShowsPages, fetchNextPage: fetchMoreShows }] =
    usePaginatedQuery({
      query: alternateTvshowQuery || GetShowsDocument,
      variables: tvshowVariables,
      pause: tvshowSkip,
    });
  const hasMoreShows = hasMoreShowsPages && !tvshowSkip;
  const tvshowDataPath = alternateTvshowPath || ['tvshows'];
  const tvshows = tvshowData?.flatMap((data) => getObjectValue(data, tvshowDataPath)?.data);

  const entities = useRef([]);

  if (!moviesLoading && !showsLoading) {
    const localMovies = movies?.length > 0 && !movieSkip ? movies : [];
    const localShows = tvshows?.length > 0 && !tvshowSkip ? tvshows : [];

    entities.current = interleave(localMovies, localShows);
  }

  let ad_count = 0;

  return (
    <div className={classProps?.root}>
      <AditionInitialization tags={adPositions} />
      {entities.current?.length > 0 ? (
        <section>
          <div className="grid grid-flow-row-dense grid-cols-fill-streaming-default gap-x-4 gap-y-16">
            {entities.current.map((entity, idx) => (
              <Fragment key={entity.id}>
                {idx > 1 && idx % 8 === 0 && idx !== 16 && ad_count < 3 && (
                  <div className="col-span-full col-start-1 grid place-items-center">
                    {/*FIXME: maybe add CAD as well*/}
                    <AditionTagWithFallback
                      tag={adPositions[8]}
                      tagCount={ad_count}
                      fallback={adPositions[24 + ad_count++]}
                      breakpoint={'lg'}
                    />
                  </div>
                )}
                <Transition
                  as="div"
                  enter="transition-opacity duration-500"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-500"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  show={true}
                  appear={true}
                >
                  <StreamingTeaser entry={entity} listicleType={entity?.type?.toUpperCase()} showUpperBookmark />
                </Transition>
              </Fragment>
            ))}
          </div>
          <div className="mt-24">
            {(hasMoreShows || hasMoreMovies) && (
              <div className="text-center">
                <Button
                  title="Mehr laden"
                  hasAction
                  onAction={() => {
                    setTimeout(() => {
                      hasMoreMovies && fetchMoreMovies();
                      hasMoreShows && fetchMoreShows();
                    });
                  }}
                  isLoading={moviesLoading || showsLoading}
                  buttonStyle="secondary"
                  classProps={{ root: 'mt-6' }}
                />
              </div>
            )}
          </div>
        </section>
      ) : (
        !moviesLoading && !showsLoading && <>{children}</>
      )}
      <LoadingIndicator visible={(moviesLoading || showsLoading) && entities.current?.length < 1} />
    </div>
  );
};
